import { FC, useEffect, useMemo, useRef } from 'react';
import { usePlyLoader, useTextureLoader } from '../meshLoadingHooks';
import { Box3, Mesh } from 'three';
import { useMeshMaterialStore } from '../MaterialControls/meshMaterialStore';
import { MetricKeyEnum, useLayerStore } from '../LayerControls/layerStore';
import { useStandingMeshStore } from './standingMeshStore';
import { XRayMaterial } from '../3dModels/XRayMaterial';

interface StandingMeshProps {
  modelUrl: string;
  textureUrl: string;
  accessToken: string;
  textureEnabled: boolean;
}

const standingMeshLayerKey = MetricKeyEnum.Standing;

export const StandingMesh: FC<StandingMeshProps> = ({
  modelUrl,
  textureUrl,
  accessToken,
  textureEnabled,
}) => {
  const object = usePlyLoader(modelUrl, accessToken);

  const { addLayerState, isLayerVisible } = useLayerStore();
  const { materialState } = useMeshMaterialStore();
  const meshRef = useRef<Mesh>(null);
  const { setStandingMeshBoundingBox } = useStandingMeshStore();

  const material = useMemo(() => {
    if (textureEnabled === false) {
      return <XRayMaterial />;
    }
    if (materialState === 'xray' || !textureUrl) {
      return <XRayMaterial />;
    }
    return (
      <StandingMeshWithTexture
        textureUrl={textureUrl}
        accessToken={accessToken}
      />
    );
  }, [materialState, textureUrl, accessToken, textureEnabled]);

  useEffect(() => {
    addLayerState(standingMeshLayerKey, {
      key: standingMeshLayerKey,
      visible: true,
    });
  }, [addLayerState]);

  useEffect(() => {
    if (meshRef.current) {
      const box = new Box3().setFromObject(meshRef.current);
      setStandingMeshBoundingBox(box);
    }
  }, [object, setStandingMeshBoundingBox]);

  const showLayer = isLayerVisible(standingMeshLayerKey);

  return (
    <group name={standingMeshLayerKey} visible={showLayer}>
      <mesh ref={meshRef} geometry={object} castShadow>
        {material}
      </mesh>
    </group>
  );
};

interface StandingMeshWithTextureProps {
  textureUrl: string;
  accessToken: string;
}

const StandingMeshWithTexture: FC<StandingMeshWithTextureProps> = ({
  textureUrl,
  accessToken,
}) => {
  const texture = useTextureLoader(textureUrl, accessToken);

  return <meshPhongMaterial map={texture} />;
};
